import { COPY_POLICIES, TITLE_COPY_LIMITS } from "./copy-policy";
/**
 * Highlights rules — the single validation point for a whole band of facts.
 *
 * Four rules, and they are four rather than one: an Editor whose second
 * highlight has no description should not be told about the title instead. Rule
 * order is the Editor's reading order down the block — the title sits above the
 * facts, so the title is checked first, and within the facts each highlight is
 * checked in the order it is read. This matches {@link validateStatement},
 * {@link validateCarousel} and {@link validateTimeline}.
 *
 * Within each part, empty and blank are ONE rule, not two. A part left empty and
 * a part of nothing but spaces and line breaks are the same failure reached by
 * different inputs, so they share `saysNothing` and they share a message.
 *
 * The TITLE IS REQUIRED, and that is this component's one non-obvious call. The
 * band renders "At a Glance" when its caller supplies no title, so an optional
 * title would not render as nothing — it would render as wording nobody chose,
 * and a band that had silently inherited it would look composed. Making it
 * required is what turns that into a refusal an Editor can act on.
 *
 * A BAND NEEDS AT LEAST ONE HIGHLIGHT. A heading with nothing under it is not a
 * band of facts. There is deliberately no upper bound: the grid wraps, so the
 * only cost of a long band is a longer band.
 *
 * TWO HIGHLIGHTS MAY SHARE A LABEL. That is a deliberate non-rule, and it is the
 * whole difference from {@link validateTimeline}, whose years must differ
 * because a year is the control a visitor travels by. Nothing here is addressed
 * by a label and nothing navigates by one, so a band may measure the same thing
 * twice. `compose-highlights.spec.ts` confirms the acceptance rather than
 * leaving it as a silence, because a silence cannot fail.
 *
 * Every part is stored EXACTLY as entered, not trimmed. `saysNothing` decides
 * whether a part says anything; it does not get to edit what the Editor wrote.
 *
 * Every part is plain words, so a band inherits no supported-content model or
 * formatting rule. A highlight description's schema-driven size ceiling is
 * delegated to the shared scalar-slot validator. There is no media rule either
 * — the band holds no pictures at all.
 *
 * Refusal is whole-payload atomic: the failing rule returns, nothing is mutated,
 * and no part is replaced.
 *
 * The schema is in `./blocks/highlights`; the rules are here.
 */
import { validateScalarSlotLength } from "./scalar-slot-validation";
/** A band needs somewhere to start. A heading with nothing under it is not one. */
export const HIGHLIGHTS_MINIMUM_ITEMS = 1;
/** The repeat group the facts live in. */
export const HIGHLIGHTS_ITEMS_KEY = "highlights";
export const HIGHLIGHT_DESCRIPTION_MAX_LENGTH = COPY_POLICIES.description.maxLength;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Does this part say anything at all? */
function saysNothing(value) {
    return typeof value !== "string" || value.trim() === "";
}
/** Validate a whole Highlights band. */
export function validateHighlights(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Highlights block is malformed" };
    }
    if (saysNothing(value.title)) {
        return { valid: false, reason: "A highlights band needs a title" };
    }
    const titleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Title" }, value.title);
    if (!titleLength.valid)
        return titleLength;
    const highlights = value.highlights;
    if (!Array.isArray(highlights)) {
        return { valid: false, reason: "The Highlights block is malformed" };
    }
    if (highlights.length < HIGHLIGHTS_MINIMUM_ITEMS) {
        return {
            valid: false,
            reason: "A highlights band needs at least one highlight",
        };
    }
    const checked = [];
    for (const highlight of highlights) {
        if (!isRecord(highlight)) {
            return { valid: false, reason: "The Highlights block is malformed" };
        }
        if (saysNothing(highlight.label)) {
            return { valid: false, reason: "Every highlight needs a label" };
        }
        if (saysNothing(highlight.description)) {
            return { valid: false, reason: "Every highlight needs a description" };
        }
        const descriptionLength = validateScalarSlotLength({ label: "Description", maxLength: HIGHLIGHT_DESCRIPTION_MAX_LENGTH }, highlight.description);
        if (!descriptionLength.valid)
            return descriptionLength;
        // Rebuilt field by field, so an unknown key cannot ride into storage.
        checked.push({
            label: highlight.label,
            description: highlight.description,
        });
    }
    return {
        valid: true,
        highlights: { title: value.title, highlights: checked },
    };
}
//# sourceMappingURL=highlights.js.map